import './style.css';
import Feature from 'ol/Feature.js';
import {Map, View} from 'ol';
import OSM from 'ol/source/OSM';
import Point from 'ol/geom/Point.js';
import {fromLonLat, toLonLat} from 'ol/proj.js';
import {Icon, Style} from 'ol/style.js';
import {toSize} from 'ol/size';

import {Vector as VectorSource} from 'ol/source.js';
import {Tile as TileLayer, Vector as VectorLayer} from 'ol/layer.js';

class DMS_OpenLayer_Map {
  constructor(mapID) {
    this.map = false;
    this.view = false;
    this.marker = false;
    this.parallax = false;
    this.fromTop = 0;
    this.scrollAmount = 0;

    this.mapMode = 'view';

    this.mapID = mapID;
    this.mapElement = document.getElementById(this.mapID);
    this.mapWrapper = false;

    this.latitude = 0;
    this.longitude = 0;
    this.zoom = 0;

    this.inputs = {
      latitude: '',
      longitude: '',
      zoom: '',
    }

    if (this.mapElement) {
      // Wrap our element
      let mapWrapper = document.createElement('div');
      mapWrapper.classList.add('dms-map-wrapper');
      mapWrapper.innerHTML = '';

      this.mapElement.parentNode.insertBefore(mapWrapper, this.mapElement);
      mapWrapper.appendChild(this.mapElement);

      this.mapWrapper = mapWrapper;

      this.latitude = parseFloat(this.mapElement.dataset.latitude) || this.latitude;
      this.longitude = parseFloat(this.mapElement.dataset.longitude) || this.longitude;
      this.zoom = parseInt(this.mapElement.dataset.zoom) || this.zoom;

      this.parallax = (typeof this.mapElement.dataset.parallax !== 'undefined' ? this.mapElement.dataset.parallax : false);

      // Setup Latitude Input
      if (this.mapElement.dataset.latitudeInput) {
        let latitudeInput = document.getElementById(this.mapElement.dataset.latitudeInput);

        if (latitudeInput) {
          this.inputs.latitude = latitudeInput;

          this.mapMode = 'edit';
        }
      }

      // Setup Longitude Input
      if (this.mapElement.dataset.longitudeInput) {
        let longitudeInput = document.getElementById(this.mapElement.dataset.longitudeInput);

        if (longitudeInput) {
          this.inputs.longitude = longitudeInput;
        }
      }

      // Setup Zoom Input
      if (this.mapElement.dataset.zoomInput) {
        let zoomInput = document.getElementById(this.mapElement.dataset.zoomInput);

        if (zoomInput) {
          this.inputs.zoom = zoomInput;
        }
      }

      this.generate();
      this.addMarker(this.latitude, this.longitude);


      // Setup Events w/ custom plug-in events

      let thisMap = this; // Required since we change scope below
      this.map.on('moveend', function (e) {
        let newZoom = e.map.getView().getZoom();

        thisMap.removeTooltip();

        if (thisMap.zoom !== newZoom) {
          thisMap.setInput('zoom', Math.round(newZoom));
          thisMap.zoom = Math.round(newZoom);
        }
      });

      // On click
      this.map.on('click', function (e) {
        const feature = e.map.forEachFeatureAtPixel(e.pixel, function (feature) {
          return feature;
        });

        if (!feature) {
          return;
        }

        thisMap.removeTooltip();

        let tooltip = document.createElement('div');

        tooltip.classList.add('dms-map-tooltip');
        tooltip.innerHTML = thisMap.mapElement.dataset.hover;

        thisMap.mapElement.parentElement.appendChild(tooltip);

        let pixel = e.map.getPixelFromCoordinate(feature.getGeometry().getCoordinates())
        let tooltipTopOffset = (parseInt(thisMap.mapElement.dataset.markerHeight));


        tooltip.style.left = pixel[0] + 'px';
        tooltip.style.top = (pixel[1] - tooltipTopOffset) + 'px';
      });

      // On double click
      this.map.on('dblclick', function (e) {
        if (thisMap.mapMode === 'view') {
          return false;
        }

        let point = e.map.getCoordinateFromPixel(e.pixel);
        let lonLat = toLonLat(point);

        thisMap.addMarker(lonLat[1], lonLat[0]);

        thisMap.setInput('latitude', lonLat[1]);
        thisMap.setInput('longitude', lonLat[0]);

        return false;
      });

      // Mouse move
      this.map.on('pointermove', function (e) {
        const pixel = e.map.getEventPixel(e.originalEvent);
        const hit = e.map.hasFeatureAtPixel(pixel);

        thisMap.mapElement.style.cursor = hit ? 'pointer' : '';
      });

      if (this.parallax) {
        let lastScrollTop = 0;
        let duration = 1000;

        window.addEventListener("scroll", this.throttle(function () {
          let currentScroll = window.pageYOffset || document.documentElement.scrollTop;
          let centerScroll = ((thisMap.view.get('resolution') * (window.scrollY - Math.abs(thisMap.scrollAmount))) * thisMap.parallax);

          if (currentScroll > lastScrollTop) {
            thisMap.animate(0, centerScroll, duration, function(currentPosition) {
              thisMap.view.adjustCenter([0, currentPosition]);
            });
          } else {
            thisMap.animate(0, centerScroll, duration, function(currentPosition) {
              thisMap.view.adjustCenter([0, -Math.abs(currentPosition)]);
            });
          }

          lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;

          thisMap.scrollAmount = window.scrollY;

        }, 100), false);
      }
    }
  }

  animate(initialPosition, targetPosition, duration, callback) {
    let startTime = null;
    let easeInOutQuad = this.easeInOutQuad;

    let previousPosition = initialPosition; // Initialize previous position

    function animate(timestamp) {
      if (!startTime) startTime = timestamp;
      let elapsedTime = timestamp - startTime;
      let progress = Math.min(1, elapsedTime / duration);
      let easingValue = easeInOutQuad(progress);
      let currentPosition = initialPosition + (targetPosition - initialPosition) * easingValue;
      let difference = currentPosition - previousPosition; // Calculate difference

      // Perform your action with the current position (e.g., update the center of the element)
      callback(difference);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }

      previousPosition = currentPosition; // Update previous position
    }

    requestAnimationFrame(animate);
  }

  throttle(callback, delay) {
    let lastCall = 0;

    return function() {
      let now = new Date().getTime();
      if (now - lastCall >= delay) {
        callback.apply(null, arguments);
        lastCall = now;
      }
    };
  }

  easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  getDistanceToCenter(element) {
    let rect = element.getBoundingClientRect();
    let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let elementTop = rect.top + scrollTop;
    let elementHeight = element.clientHeight;
    let windowHeight = window.innerHeight;

    let distanceToTop = elementTop - scrollTop;

    let distanceFromCenter = distanceToTop + (elementHeight / 2) - (windowHeight / 2);

    return distanceFromCenter;
  }

  generate() {
    if (this.mapElement) {
      this.view = new View({
        center: fromLonLat([this.longitude, this.latitude]),
        zoom: this.zoom
      });

      let mapProperties = {
        target: this.mapID,
        layers: [
          new TileLayer({
            source: new OSM(),
            preload: 5
          }),
        ],
        view: this.view
      };

      this.map = new Map(mapProperties);

      if (this.parallax) {
        this.fromTop = this.getDistanceToCenter(this.mapElement);

        let offset = -(this.view.get('resolution') * this.fromTop) * this.parallax;

        // Set the new center coordinates for the map
        this.map.getView().adjustCenter([0, (offset)]);
      }

      this.setInput('latitude', this.latitude);
      this.setInput('longitude', this.longitude);
      this.setInput('zoom', this.zoom);
    }
  }

  /**
   * Remove previous tooltip so we only display 1
   */
  removeTooltip() {
    let tooltip = this.mapWrapper.querySelector('.dms-map-tooltip');

    if (tooltip) {
      tooltip.remove();
    }
  }

  /**
   * Format a coordinate to only 5 digits
   *
   * @param coord
   * @returns {string}
   */
  formatCoord(coord) {
    return parseFloat(coord).toFixed(5);
  }

  /**
   * Add marker to map
   *
   * @param latitude
   * @param longitude
   */
  addMarker(latitude, longitude) {
    //console.log('marker', this.marker)
    let markerWidth = parseInt(this.mapElement.dataset.markerWidth);
    let markerHeight = parseInt(this.mapElement.dataset.markerHeight);
    let markerImg = this.mapElement.dataset.markerImg;

    if (this.marker) {
      this.map.removeLayer(this.marker);
    }

    const iconFeature = new Feature({
      geometry: new Point(fromLonLat([longitude, latitude])),
    });

    const iconStyle = new Style({
      image: new Icon({
        anchor: [0.5, markerHeight],
        anchorXUnits: 'fraction',
        anchorYUnits: 'pixels',
        src: markerImg,
        size: new toSize([markerWidth, markerHeight]),
      }),
    });

    iconFeature.setStyle(iconStyle);

    const vectorSource = new VectorSource({
      features: [iconFeature],
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
    });

    this.marker = vectorLayer;
    this.map.addLayer(vectorLayer);
  }

  /**
   * Animate the map view
   */
  setView() {
    this.view.animate({
      zoom: parseInt(this.zoom),
      center: fromLonLat([this.longitude, this.latitude])
    });

    this.setInput('latitude', this.latitude);
    this.setInput('longitude', this.longitude);
    this.setInput('zoom', this.zoom);

    this.addMarker(this.latitude, this.longitude);
  }

  /**
   * Set the latitude
   *
   * @param latitude
   * @param animation
   */
  setLatitude(latitude, animation) {
    this.latitude = parseFloat(latitude);

    if (animation) {
      this.setView();
    }
  }

  /**
   * Set the longitude
   *
   * @param longitude
   * @param animation
   */
  setLongitude(longitude, animation) {
    this.longitude = parseFloat(longitude);

    if (animation) {
      this.setView();
    }
  }

  /**
   * Set the zoom
   *
   * @param zoomLevel
   * @param animation
   */
  setZoom(zoomLevel, animation) {
    this.zoom = parseInt(zoomLevel);

    if (animation) {
      this.setView();
    }
  }

  /**
   * Set the connected input
   *
   * @param inputName
   * @param value
   */
  setInput(inputName, value) {
    if (this.mapMode === 'view') {
      return false;
    }

    if (inputName === 'latitude' || inputName === 'longitude') {
      value = this.formatCoord(value);
    }

    let changeEvent = new Event('change');

    this.inputs[inputName].value = value;
    this.inputs[inputName].dispatchEvent(changeEvent);
  }
}

function DMS_GenerateMap(mapID) {
  return new DMS_OpenLayer_Map(mapID);
}

window['DMS_GenerateMap'] = DMS_GenerateMap;